import { Injectable, inject } from '@angular/core';
import { OidcSecurityService, PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';

/**
 * Service that keeps IVION SDK token synchronized with OIDC token.
 *
 * Problem: IVION SDK receives token at initialization but doesn't auto-refresh.
 * After token expiration (~5-15 min), IVION returns 403 errors.
 *
 * Solution: Subscribe to OIDC token changes and update IVION via ivApi.auth.updateToken().
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private oidcSecurityService = inject(OidcSecurityService);
  private eventsService = inject(PublicEventsService);
  private isInitialized = false;

  /**
   * Initialize token refresh subscription.
   * Should be called after Angular bootstrap and IVION initialization.
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('[TokenRefresh] Already initialized');
      return;
    }

    // Skip in dev mode without auth
    if (environment.skipAuth) {
      console.log('[TokenRefresh] Skipped (dev mode without auth)');
      return;
    }

    this.isInitialized = true;

    // Subscribe to OIDC events - this is more reliable than getIdToken() Observable
    this.eventsService.registerForEvents().subscribe({
      next: (event) => {
        if (event.type === EventTypes.SilentRenewStarted) {
          console.log('[TokenRefresh] Silent renew started');
        } else if (event.type === EventTypes.NewAuthenticationResult) {
          console.log('[TokenRefresh] New authentication result received');
          // Fetch the new token after successful refresh
          this.oidcSecurityService.getIdToken().subscribe({
            next: (idToken) => {
              if (idToken) {
                this.updateIvionToken(idToken);
              }
            },
          });
        } else if (event.type === EventTypes.TokenExpired) {
          console.warn('[TokenRefresh] Token expired!');
        }
      },
    });

    console.log('[TokenRefresh] Initialized - watching for token changes');
  }

  /**
   * Updates IVION SDK with new token.
   * Note: IVION SDK stores updateToken on auth.authenticationService, not directly on auth
   */
  private updateIvionToken(token: string): void {
    const ivionApp = (window as any).IvionApp;

    if (!ivionApp?.ivApi?.auth?.authenticationService) {
      console.warn('[TokenRefresh] IVION not initialized yet, skipping token update');
      return;
    }

    try {
      ivionApp.ivApi.auth.authenticationService.updateToken(token);
      console.log('[TokenRefresh] Token updated successfully');
    } catch (error) {
      console.error('[TokenRefresh] Failed to update token:', error);
    }
  }
}
